import { Box, Grid, Paper, Stack, Typography } from '@mui/material'
import SyncIcon from '@mui/icons-material/Sync'
import { useSelector } from 'react-redux'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { addDays, endOfISOWeek, endOfMonth, format, setYear, subDays, startOfMonth } from 'date-fns'
import actionsService from '../../../services/actionsService'
import {
  setWallChartSelected,
  setWallChartSelectable,
  setSelectedTimespan,
  setLastSelectedMonthTimespan,
  setLastSelectedWeekTimespan,
  timeSpanInitialState,
  setLastSelectedDayTimespan,
  setWallChartSelectedTeam,
} from '../../../redux/reducers/wallChartReducer'
import { RootStore, useAppDispatch } from '../../../redux/store'
import {
  DepartmentValuesResponse,
  Employee,
  SelectOption,
  Team,
} from '../../../services/dashboardService'
import AutocompleteList from '../../../shared/UI/AutocompleteList'
import {
  getYearSelectOptions,
  getFilteredDepartmentsDropdownOptions,
  getDepartmentToSelect,
  YearRequest,
} from '../../../utils/app-utils'
import {
  HSS_YEARS_IN_THE_PAST,
  HTL_YEARS_IN_THE_PAST,
  WEEKDAYS,
  HSS_YEARS_IN_THE_FUTURE,
  HTL_YEARS_IN_THE_FUTURE,
  MONTHS,
} from '../../../utils/constants'
import { WallChartFiltersProps } from './types'
import { setSelectedDepartment } from '../../../redux/reducers/appSettingsReducer'
import { WallChartTimespans } from '../WallChartTimespanSelector/types'
import Button from '../../../shared/UI/Button'

export function WallChartFilters({ directReportEnabled, marginTop }: WallChartFiltersProps) {
  const dispatch = useAppDispatch()

  const { selectableParams, selectedParams, selectedTimeSpan } = useSelector(
    (state: RootStore) => state.wallChart
  )

  const {
    departmentTeams,
    calendarDetailsResponse,
    currentEntitlementPeriodResponse,
    directReports,
    allEmployees,
    departments,
    allTeams,
    loggedInEmployeeTeam,
  } = useSelector((state: RootStore) => state.appSettings)

  const userDepartment = useSelector<RootStore, SelectOption | null>(
    (state: RootStore) => state.appSettings.loggedInEmployeeDepartment
  )

  const localDepartments = useMemo(
    () => getFilteredDepartmentsDropdownOptions(
      departments,
      allEmployees,
      true,
      userDepartment ? [userDepartment.value] : []
    ),
    [departments, allEmployees, userDepartment]
  )

  const departmentValuesResponse = useSelector<RootStore, DepartmentValuesResponse | undefined>(
    (state: RootStore) => state.appSettings.departmentValuesResponse
  )

  const userPermissions = useSelector<RootStore, string[]>(
    (state: RootStore) => state.userState.permissions
  )

  const isManager = useSelector<RootStore, string[]>(
    (state: RootStore) => state.userState.permissions
  ).includes('ViewWhoAwayManager')

  const canViewDirectReports = userPermissions.includes('ViewManagerDirectReport')

  const handleDepartmentChange = useCallback(
    (event: unknown, department: SelectOption | null) => {
      if (department?.value === selectedParams.selectedDepartment?.value) {
        return
      }
      dispatch(setSelectedDepartment(department))
      dispatch(
        setWallChartSelected({
          ...selectedParams,
          selectedDepartment: department,
          selectedTeams: [],
          selectedEmployees: [],
        })
      )
    },
    [dispatch, selectedParams]
  )

  const handleTeamChange = (event: unknown, teams: Team[] | null) => {
    if (teams === selectedParams.selectedTeams) {
      return
    }
    actionsService.userAnalytics
      .postEvent('UIClickEvent', 'WallChartTeamDropdownChange', window.location.pathname, window.location.hostname)
      .catch((error: unknown) => console.error('Analytics tracking error:', error))
    dispatch(setWallChartSelectedTeam(teams || []))
  }

  const handleEmployeesChange = (event: unknown, chosenEmployees: Employee[] | null) => {
    actionsService.userAnalytics
      .postEvent('UIClickEvent', 'WallChartEmployeeSearchDropdownChange', window.location.pathname, window.location.hostname)
      .catch((error: unknown) => console.error('Analytics tracking error:', error))
    dispatch(setWallChartSelected({ ...selectedParams, selectedEmployees: chosenEmployees || [] }))
  }

  useEffect(() => {
    if (localDepartments.length === 0 && !userDepartment) {
      return
    }

    const toSelect = getDepartmentToSelect(
      localDepartments,
      selectedParams.selectedDepartment,
      userDepartment,
      isManager || canViewDirectReports,
      canViewDirectReports && selectedParams.directReport
    ) ?? userDepartment
    handleDepartmentChange(null, toSelect)

    if (!isManager && loggedInEmployeeTeam !== null) {
      handleTeamChange(null, [loggedInEmployeeTeam])
    }
  }, [localDepartments, loggedInEmployeeTeam, userDepartment, isManager, canViewDirectReports, selectedParams.directReport])

  const ensurePreviousMonday = (date: Date) => {
    let localDate = new Date(date)
    localDate = localDate.getDay() === 0 ? subDays(localDate, 1) : localDate
    while (localDate.getDay() > 1) {
      localDate = subDays(localDate, 1)
    }
    return localDate
  }

  const checkEndDateWithinPermittedRange = (endDate: Date) => {
    if (!calendarDetailsResponse) {
      return { endDate }
    }

    const lastBookableDate = new Date(
      calendarDetailsResponse.calendarDetails[
        calendarDetailsResponse.calendarDetails.length - 1
      ].endDate
    )

    if (new Date(endDate) > new Date(lastBookableDate)) {
      switch (selectedTimeSpan.rangeType) {
        case WallChartTimespans.DAY:
          return {
            startDate: lastBookableDate,
            endDate: lastBookableDate,
            month: MONTHS[new Date(lastBookableDate).getDate()],
          }
        case WallChartTimespans.WEEK:
          return {
            startDate: ensurePreviousMonday(lastBookableDate),
            endDate: endOfISOWeek(lastBookableDate),
            month: WEEKDAYS[lastBookableDate.getDay()],
          }
        case WallChartTimespans.TWOWEEK:
          return {
            startDate: ensurePreviousMonday(lastBookableDate),
            endDate: addDays(ensurePreviousMonday(lastBookableDate), 14),
            month: WEEKDAYS[lastBookableDate.getDay()],
          }
        case WallChartTimespans.MONTH:
        default:
          return {
            startDate: startOfMonth(new Date(format(new Date(lastBookableDate), 'yyyy-MM-dd'))),
            endDate: endOfMonth(new Date(lastBookableDate)),
            month: MONTHS[lastBookableDate.getMonth()],
          }
      }
    }

    return { endDate }
  }

  const checkStartDateWithinPermittedRange = (startDate: Date) => {
    if (!calendarDetailsResponse) {
      return { startDate }
    }

    const firstBookableDate = new Date(calendarDetailsResponse.calendarDetails[0].startDate)

    if (new Date(startDate) < new Date(firstBookableDate)) {
      switch (selectedTimeSpan.rangeType) {
        case WallChartTimespans.DAY:
          return {
            startDate: firstBookableDate,
            endDate: firstBookableDate,
            month: MONTHS[new Date(firstBookableDate).getDate()],
          }
        case WallChartTimespans.WEEK:
          return {
            startDate: ensurePreviousMonday(firstBookableDate),
            endDate: endOfISOWeek(firstBookableDate),
            month: WEEKDAYS[firstBookableDate.getDay()],
          }
        case WallChartTimespans.TWOWEEK:
          return {
            startDate: ensurePreviousMonday(firstBookableDate),
            endDate: addDays(ensurePreviousMonday(firstBookableDate), 14),
            month: WEEKDAYS[firstBookableDate.getDay()],
          }
        case WallChartTimespans.MONTH:
        default:
          return {
            startDate: startOfMonth(new Date(format(new Date(firstBookableDate), 'yyyy-MM-dd'))),
            endDate: endOfMonth(new Date(firstBookableDate)),
            month: MONTHS[firstBookableDate.getMonth()],
          }
      }
    }

    return { startDate }
  }

  const handleYearChange = (event: unknown, year: SelectOption | null) => {
    if (!calendarDetailsResponse) {
      return
    }
    actionsService.userAnalytics
      .postEvent('UIClickEvent', 'WallChartYearDropdownChange', window.location.pathname, window.location.hostname)
      .catch((error: unknown) => console.error('Analytics tracking error:', error))
    const yearDiff = Number(selectedParams?.selectedYear?.value) !== Number(year?.value)
    dispatch(setWallChartSelected({ ...selectedParams, selectedYear: year }))
    if (yearDiff) {
      let startDate = setYear(selectedTimeSpan.startDate, Number(year?.value))
      let endDate
      let dayOfWeek = selectedTimeSpan.title
      switch (selectedTimeSpan.rangeType) {
        case WallChartTimespans.DAY: {
          endDate = startDate
          dayOfWeek = WEEKDAYS[startDate.getDay()]
          break
        }
        case WallChartTimespans.WEEK: {
          startDate = ensurePreviousMonday(startDate)
          endDate = endOfISOWeek(startDate)
          dayOfWeek = WEEKDAYS[startDate.getDay()]
          break
        }
        case WallChartTimespans.TWOWEEK: {
          startDate = ensurePreviousMonday(startDate)
          endDate = addDays(startDate, 14)
          break
        }
        case WallChartTimespans.MONTH:
        default:
          endDate = endOfMonth(startDate)
          break
      }

      const lastDayCheck = checkEndDateWithinPermittedRange(endDate)
      const firstDayCheck = checkStartDateWithinPermittedRange(startDate)

      if (
        lastDayCheck.endDate.getFullYear() ===
        new Date(
          calendarDetailsResponse.calendarDetails[
            calendarDetailsResponse.calendarDetails.length - 1
          ].endDate
        ).getFullYear()
      ) {
        dispatch(
          setSelectedTimespan({
            ...selectedTimeSpan,
            startDate: lastDayCheck.startDate || startDate,
            endDate: lastDayCheck.endDate,
            title: lastDayCheck.month || dayOfWeek,
          })
        )
      } else {
        dispatch(
          setSelectedTimespan({
            ...selectedTimeSpan,
            startDate: firstDayCheck.startDate,
            endDate: firstDayCheck.endDate || endDate,
            title: firstDayCheck.month || dayOfWeek,
          })
        )
      }
    }

    dispatch(setLastSelectedDayTimespan(undefined))
    dispatch(setLastSelectedWeekTimespan(undefined))
    dispatch(setLastSelectedMonthTimespan(undefined))
  }

  const getYears = () => {
    if (
      !selectedParams.selectedDepartment ||
      calendarDetailsResponse === undefined ||
      !currentEntitlementPeriodResponse
    ) {
      return []
    }

    const yearRequest: YearRequest = {
      entitlementPeriod: 'CalendarYear',
      yearsInPast:
        calendarDetailsResponse.calendarDetails.length > 4
          ? HTL_YEARS_IN_THE_PAST
          : HSS_YEARS_IN_THE_PAST,
      yearsInFuture:
        calendarDetailsResponse.calendarDetails.length > 4
          ? HTL_YEARS_IN_THE_FUTURE
          : HSS_YEARS_IN_THE_FUTURE,
    }

    return getYearSelectOptions(yearRequest, currentEntitlementPeriodResponse.entitlementPeriod)
  }

  const isTeamsDropdownDisabled = !directReportEnabled || (!isManager && loggedInEmployeeTeam !== null) || selectedParams.selectedDepartment?.value === 0

  const resetWallChartFilters = () => {
    actionsService.userAnalytics
      .postEvent('UIClickEvent', 'WallChartFiltersResetClick', window.location.pathname, window.location.hostname)
      .catch((error: unknown) => console.error('Analytics tracking error:', error))
    if (userDepartment) {
      const nowYear = new Date().getFullYear()
      dispatch(
        setWallChartSelected({
          ...selectedParams,
          selectedDepartment: selectedParams.selectedDepartment,
          selectedTeams: isTeamsDropdownDisabled ? selectedParams.selectedTeams : [],
          selectedEmployees: [],
          selectedYear: selectedParams.selectedYear
            ? { ...selectedParams.selectedYear }
            : {
                displayValue: String(nowYear),
                value: nowYear,
              },
        })
      )
    }
    dispatch(setSelectedTimespan(timeSpanInitialState))
    if (userPermissions.includes('ViewManagerDirectReport')) {
      const all = localDepartments.find(d => d.displayValue === 'All')
      if (all) {
        handleDepartmentChange(null, all)
      }
    }
  }

  return (
    <Paper
      sx={{
        marginTop,
        backgroundColor: "#FCFCFC",
        padding: "24px",
        borderRadius: "8px",
        border: "1px solid #EBEBEB",
        boxShadow: "none",
        "& .MuiAutocomplete-root": {
          backgroundColor: "white"
        }
      }}
    >
      <Stack
        spacing={3}
        direction={{ xs: 'column', md: 'row' }}
      >
        {userPermissions.includes('ViewWallChartDepartments') && (
          <Box flexBasis="25%" flexGrow={3} flexShrink={1}>
            <AutocompleteList
              id="departments"
              label="Department"
              data={localDepartments}
              textField="displayValue"
              disabled={!directReportEnabled || localDepartments.length === 1}
              onChange={handleDepartmentChange}
              value={selectedParams.selectedDepartment}
              dataTestId="WallChart-DepartmentDDL"
              isOptionEqualToValue={(option, value) => option.value === value.value}
            />
          </Box>
        )}
        <Box flexBasis="25%" flexGrow={3} flexShrink={1}>
          <AutocompleteList
            id="teams"
            label="Team"
            data={departmentTeams}
            textField="teamName"
            disabled={isTeamsDropdownDisabled}
            onChange={handleTeamChange}
            multiple
            value={selectedParams.selectedTeams}
            limitTags={1}
            dataTestId="WallChart-TeamsDDL"
            isOptionEqualToValue={(option, value) => option.teamId === value.teamId}
          />
        </Box>
        <Box flexBasis="25%" flexGrow={3} flexShrink={1}>
          <AutocompleteList
            multiple
            id="employees"
            label="Employee Search"
            data={selectableParams.availableEmployees}
            textField="employeeName"
            disabled={!directReportEnabled}
            onChange={handleEmployeesChange}
            value={selectedParams.selectedEmployees}
            limitTags={1}
            dataTestId="WallChart-EmployeeDDL"
            isOptionEqualToValue={(option, value) => option.employeeId === value.employeeId}
          />
        </Box>
        <Box flexBasis="15%" flexGrow={2} flexShrink={2}>
          <AutocompleteList
            id="years"
            label="Year"
            disableClearable
            data={getYears()}
            textField="displayValue"
            disabled={!directReportEnabled}
            onChange={handleYearChange}
            value={selectedParams.selectedYear}
            dataTestId="WallChart-YearDDL"
            isOptionEqualToValue={(option, value) => option.value === value.value}
          />
        </Box>
        <Button
          variant="text"
          label={
            <Box display="flex" alignItems="center">
              <SyncIcon fontSize="small" style={{ marginRight: '8px' }} />
              <Typography variant="body1" component="span" fontWeight="600">Reset</Typography>
            </Box>
          }
          onClick={resetWallChartFilters}
        />
      </Stack>
    </Paper>
  )
}
